import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { api } from '../api';

const PLACEHOLDER_IMG = 'https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?w=800&h=500&fit=crop';

const CATEGORY_COLORS = {
  'umrah-tips':    { bg: 'bg-emerald-100', text: 'text-emerald-800' },
  'packing-guide': { bg: 'bg-amber-100',   text: 'text-amber-800'   },
  'visa-updates':  { bg: 'bg-blue-100',    text: 'text-blue-800'    },
  'dua-guide':     { bg: 'bg-purple-100',  text: 'text-purple-800'  },
  'news':          { bg: 'bg-red-100',     text: 'text-red-800'     },
  'travel-guide':  { bg: 'bg-teal-100',    text: 'text-teal-800'    },
  'hajj-tips':     { bg: 'bg-orange-100',  text: 'text-orange-800'  },
};

const CategoryBadge = ({ category, label }) => {
  const c = CATEGORY_COLORS[category] || { bg: 'bg-sand', text: 'text-ink' };
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wide uppercase ${c.bg} ${c.text}`}>
      {label}
    </span>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric' });
};

const BlogPage = ({ navigate }) => {
  const [posts, setPosts]           = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch]         = useState('');
  const [loading, setLoading]       = useState(true);
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, search]);

  const loadCategories = async () => {
    try {
      const data = await api.getBlogCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (_) {}
  };

  const loadPosts = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (activeCategory !== 'all') filters.category = activeCategory;
      if (search) filters.search = search;
      const data = await api.getBlogPosts(filters);
      setPosts(data);
    } catch (_) {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
  };

  const featured = posts.find((p) => p.is_featured) || posts[0];
  const rest = featured ? posts.filter((p) => p.id !== featured.id) : posts;

  return (
    <div className="min-h-screen bg-cream">
      <Navbar navigate={navigate} />

      {/* Hero */}
      <section className="relative bg-ink text-white pt-28 pb-16 sm:pt-36 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #C9A45C 0, transparent 50%), radial-gradient(circle at 80% 20%, #C9A45C 0, transparent 40%)' }} />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <p className="text-accent text-[11px] tracking-[0.28em] uppercase mb-4">Knowledge &amp; Guidance</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl mb-4">Blog &amp; News</h1>
          <p className="text-sand/70 text-lg max-w-2xl mx-auto">
            Umrah tips, packing guides, visa updates, and spiritual guidance — everything to prepare you for the journey.
          </p>

          {/* Search */}
          <form onSubmit={handleSearch} className="mt-8 flex gap-2 max-w-lg mx-auto">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search articles..."
              className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-sand/50 focus:outline-none focus:border-accent text-sm"
            />
            {searchInput && (
              <button type="button" onClick={clearSearch}
                className="px-3 py-3 text-sand/60 hover:text-white transition-colors">✕</button>
            )}
            <button type="submit"
              className="gradient-gold text-ink px-5 py-3 rounded-lg font-semibold text-sm">
              Search
            </button>
          </form>
        </div>
      </section>

      {/* Category Tabs */}
      <div className="bg-white border-b border-sand sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-2 scrollbar-hide">
            <button
              onClick={() => setActiveCategory('all')}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === 'all'
                  ? 'bg-primary text-white'
                  : 'text-ink/60 hover:text-primary hover:bg-cream'
              }`}>
              All Articles
              {categories.length > 0 && (
                <span className="ml-1.5 text-xs opacity-70">
                  ({categories.reduce((s, c) => s + c.count, 0)})
                </span>
              )}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setActiveCategory(cat.value)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat.value
                    ? 'bg-primary text-white'
                    : 'text-ink/60 hover:text-primary hover:bg-cream'
                }`}>
                {cat.label}
                <span className="ml-1.5 text-xs opacity-70">({cat.count})</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12 sm:py-16">

        {/* Search result note */}
        {search && (
          <div className="mb-8 flex items-center gap-3">
            <p className="text-ink/60 text-sm">
              Showing results for <span className="font-semibold text-ink">"{search}"</span>
              {' '}— {posts.length} article{posts.length !== 1 ? 's' : ''} found
            </p>
            <button onClick={clearSearch} className="text-primary text-sm underline">Clear</button>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                <div className="h-48 bg-sand" />
                <div className="p-5 space-y-3">
                  <div className="h-3 bg-sand rounded w-24" />
                  <div className="h-5 bg-sand rounded w-full" />
                  <div className="h-5 bg-sand rounded w-3/4" />
                  <div className="h-3 bg-sand rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-5xl mb-4">📝</p>
            <h3 className="text-xl font-semibold text-ink mb-2">No articles yet</h3>
            <p className="text-ink/50">Check back soon — new guides and tips are on the way.</p>
          </div>
        ) : (
          <>
            {/* Featured post — only show on "all" tab with no search */}
            {featured && activeCategory === 'all' && !search && (
              <Reveal className="mb-12">
                <div
                  onClick={() => navigate('blog-detail', { slug: featured.slug })}
                  className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow flex flex-col lg:flex-row">
                  <div className="lg:w-1/2 aspect-[16/9] lg:aspect-auto overflow-hidden bg-sand">
                    <img
                      src={featured.featured_image || PLACEHOLDER_IMG}
                      alt={featured.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
                    />
                  </div>
                  <div className="lg:w-1/2 p-8 lg:p-10 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <CategoryBadge category={featured.category} label={featured.category_display} />
                      <span className="text-xs text-accent font-semibold tracking-wide uppercase bg-accent/10 px-2.5 py-1 rounded-full">
                        Featured
                      </span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-semibold text-ink leading-tight mb-3 group-hover:text-primary transition-colors">
                      {featured.title}
                    </h2>
                    <p className="text-ink/60 leading-relaxed mb-6 line-clamp-3">{featured.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-ink">{featured.author_name}</p>
                        <p className="text-xs text-ink/40 mt-0.5">
                          {formatDate(featured.published_at)} · {featured.read_time_minutes} min read
                        </p>
                      </div>
                      <span className="gradient-gold text-ink px-5 py-2 rounded-lg text-sm font-semibold">
                        Read →
                      </span>
                    </div>
                  </div>
                </div>
              </Reveal>
            )}

            {/* Post grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(activeCategory === 'all' && !search ? rest : posts).map((post, i) => (
                <Reveal key={post.id} delay={i * 60}>
                  <div
                    onClick={() => navigate('blog-detail', { slug: post.slug })}
                    className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
                    <div className="aspect-[16/10] overflow-hidden bg-sand">
                      <img
                        src={post.featured_image || PLACEHOLDER_IMG}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
                      />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <CategoryBadge category={post.category} label={post.category_display} />
                        <span className="text-xs text-ink/40">{post.read_time_minutes} min</span>
                      </div>
                      <h3 className="text-lg font-semibold text-ink leading-snug mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-sm text-ink/55 leading-relaxed line-clamp-3 flex-1">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-sand">
                        <p className="text-xs text-ink/40">{formatDate(post.published_at)}</p>
                        <span className="text-primary text-sm font-semibold group-hover:gap-2 flex items-center gap-1 transition-all">
                          Read <span className="group-hover:translate-x-1 transition-transform">→</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </>
        )}
      </div>

      <Footer navigate={navigate} />
    </div>
  );
};

export default BlogPage;
